/**
 * StepProject — Etapa 1: Tipo e dados básicos do projeto.
 *
 * Persistência real: POST /projects (criar) ou PATCH /projects/{id} (atualizar).
 * Brownfield vs Greenfield: ao escolher Greenfield, aviso que a etapa Ideação
 * é adicionada ao fluxo (fiel ao protótipo).
 *
 * Paleta C, estilos inline, pt-BR, a11y preservados.
 */
import { useState, useEffect, useRef } from 'react';
import { Cpu, Globe } from 'lucide-react';
import { C } from '@/theme/palette';
import { Card, Btn, Badge } from '@/components/ui';
import { MutationError } from '@/components/ui/QueryState';
import { useCreateProject, useUpdateProject, useProject } from '@/api/projects';
import { useJourney } from '../JourneyContext';

// ─── Constantes ───────────────────────────────────────────────────────────────

const TYPE_CARDS = [
  {
    id:          'brownfield',
    title:       'Brownfield',
    subtitle:    'Evolução de código existente',
    icon:        Cpu,
    color:       C.cyber,
    description: 'Squad utiliza o "Repo Knowledge Pack" para respeitar padrões legados, ADRs e convenções do repositório.',
  },
  {
    id:          'greenfield',
    title:       'Greenfield',
    subtitle:    'Projeto do zero',
    icon:        Globe,
    color:       C.quantum,
    description: 'Projeto novo. Adiciona a etapa de Ideação (assistida por IA) para capturar a visão de produto.',
  },
];

// Modo do contrato (D1 do plano Contract-Driven). Editável depois da criação — ao
// contrário do tipo do projeto, trocar o modo não muda as etapas da jornada.
const CONTRACT_MODES = [
  {
    id:    'hybrid',
    label: 'Híbrido',
    hint:  'Gera o contrato e aponta as lacunas, mas a entrega segue com o estado de revisão.',
  },
  {
    id:    'strict',
    label: 'Estrito',
    hint:  'Lacuna de especificação bloqueia a geração; o envio ao GitHub exige entrega pronta para revisão.',
  },
];

// Modo de entrega — só greenfield. `client` não combina com o contrato `strict` (a API
// recusa com 422), então cada opção desabilita a outra com o motivo à vista.
const DELIVERY_MODES = [
  {
    id:    'platform',
    label: 'Validar na plataforma',
    hint:  'A plataforma sobe o projeto gerado e roda as provas antes de entregar.',
  },
  {
    id:    'client',
    label: 'Finalizar no cliente',
    hint:  'Entrega mais rápida, com o kit de finalização para o agente de IA do cliente validar e corrigir na máquina dele.',
  },
];

// ─── StepProject ──────────────────────────────────────────────────────────────

export function StepProject({ onNext }) {
  const {
    projectId, projectType, deliveryScope,
    setProjectId, setProjectType, setDeliveryScope,
  } = useJourney();

  const [name,        setName]        = useState('');
  const [description, setDescription] = useState('');
  const [localType,   setLocalType]   = useState(projectType);
  const [contractMode, setContractMode] = useState('hybrid');
  const [deliveryModeEscolhido, setDeliveryMode] = useState('platform');
  const [briefError,  setBriefError]  = useState('');

  // Em 'analise', a Descrição vira o BRIEF da análise ("o que quero descobrir") —
  // campo grande e principal; é o objetivo que o pipeline endereça (salvo em
  // projects.description e injetado nos prompts da análise).
  const isAnalise = deliveryScope === 'analise';

  const createProject = useCreateProject();
  const updateProject = useUpdateProject(projectId);

  const mutation = projectId ? updateProject : createProject;

  // Sincroniza localType com contexto externo (se voltar à etapa)
  useEffect(() => { setLocalType(projectType); }, [projectType]);

  // HIDRATA os campos de um projeto EXISTENTE (ex.: reabrir pela Gestão do Projeto).
  // Sem isto, o form abria vazio ("não mostra os dados do projeto") mesmo com o
  // projectId correto. Hidrata uma vez por projeto (ref), sem sobrescrever edições.
  const { data: existing } = useProject(projectId);
  const hydratedFor = useRef(null);
  useEffect(() => {
    if (!existing || !projectId || hydratedFor.current === projectId) return;
    hydratedFor.current = projectId;
    setName(existing.name || '');
    setDescription(existing.description || '');
    if (existing.project_type) {
      setLocalType(existing.project_type);
      setProjectType(existing.project_type);
    }
    if (existing.delivery_scope) setDeliveryScope(existing.delivery_scope);
    if (existing.contract_mode) setContractMode(existing.contract_mode);
    if (existing.delivery_mode) setDeliveryMode(existing.delivery_mode);
  }, [existing, projectId, setProjectType, setDeliveryScope]);

  // O tipo é definido na CRIAÇÃO e o servidor não o altera depois (2026-09-23): trocar
  // aqui mudava as etapas da tela, mas a execução seguia com o tipo gravado — a tela
  // mostrava um fluxo e o codegen rodava outro.
  const tipoTravado = Boolean(projectId && existing?.project_type);
  const [avisoTipo, setAvisoTipo] = useState('');
  function handleTypeSelect(type) {
    if (tipoTravado && type !== existing.project_type) {
      setAvisoTipo('O tipo do projeto é definido na criação e não pode ser trocado. '
        + 'Para outro tipo, crie um projeto novo.');
      return;
    }
    setAvisoTipo('');
    if (type !== 'greenfield') setDeliveryMode('platform');
    setLocalType(type);
    setProjectType(type);
  }

  // Fora do greenfield o modo é sempre `platform`, venha o tipo de onde vier.
  const deliveryMode = localType === 'greenfield' ? deliveryModeEscolhido : 'platform';

  async function handleSubmit(e) {
    e.preventDefault();

    // Em análise, o brief (Escopo da Análise) é obrigatório — é o objetivo que a
    // execução vai endereçar. O form usa noValidate, então validamos aqui.
    if (isAnalise && !description.trim()) {
      setBriefError('Descreva o que você precisa analisar para continuar.');
      return;
    }
    setBriefError('');

    const body = {
      name:           name.trim() || 'Novo Projeto',
      description:    description.trim(),
      project_type:   localType,
      delivery_scope: deliveryScope,
      // Só manda o modo quando ele é conhecido: num projeto existente ainda carregando, o
      // estado local é o default 'hybrid' e rebaixaria um projeto 'strict' sem querer.
      ...(!projectId || hydratedFor.current === projectId
        ? { contract_mode: contractMode, delivery_mode: deliveryMode } : {}),
    };

    try {
      let result;
      if (projectId) {
        result = await updateProject.mutateAsync(body);
      } else {
        result = await createProject.mutateAsync(body);
        if (result?.id) setProjectId(result.id);
      }
      onNext?.();
    } catch {
      // erro tratado pelo MutationError abaixo
    }
  }

  const isLoading = mutation.isPending;
  const error     = mutation.error;

  return (
    <form onSubmit={handleSubmit} noValidate>
      {/* Título da etapa */}
      <div style={{ marginBottom: 28 }}>
        <h2 style={{ fontSize: 20, fontWeight: 600, color: C.neutral, margin: '0 0 6px' }}>
          Tipo do Projeto
        </h2>
        <p style={{ fontSize: 14, color: C.neutralDim, margin: 0 }}>
          Escolha o tipo de projeto e preencha as informações básicas.
        </p>
      </div>

      {/* Erro de mutation */}
      <MutationError error={error} onDismiss={() => mutation.reset()} />

      {/* Cards de tipo */}
      <div
        className="grid gap-4 mb-8"
        style={{ gridTemplateColumns: '1fr 1fr' }}
        role="radiogroup"
        aria-label="Tipo do projeto"
      >
        {TYPE_CARDS.map((card) => {
          const Icon    = card.icon;
          const selected = localType === card.id;

          return (
            <button
              key={card.id}
              type="button"
              role="radio"
              aria-checked={selected}
              onClick={() => handleTypeSelect(card.id)}
              aria-disabled={tipoTravado && !selected}
              title={tipoTravado && !selected ? 'O tipo é definido na criação do projeto' : undefined}
              style={{
                backgroundColor: selected ? `${card.color}11` : C.blackElev,
                border: `2px solid ${selected ? card.color : C.blackBorder}`,
                borderRadius: 10,
                padding: 24,
                cursor: tipoTravado && !selected ? 'not-allowed' : 'pointer',
                opacity: tipoTravado && !selected ? 0.5 : 1,
                textAlign: 'left',
                transition: 'all 150ms',
              }}
            >
              <div className="flex items-start justify-between mb-4">
                <div
                  style={{
                    width: 44,
                    height: 44,
                    borderRadius: 8,
                    backgroundColor: `${card.color}22`,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Icon size={20} style={{ color: card.color }} aria-hidden="true" />
                </div>
                {selected && (
                  <Badge color={card.color} light>SELECIONADO</Badge>
                )}
              </div>
              <div style={{ fontSize: 16, fontWeight: 600, color: C.neutral, marginBottom: 4 }}>
                {card.title}
              </div>
              <div style={{ fontSize: 13, color: card.color, marginBottom: 8 }}>
                {card.subtitle}
              </div>
              <div style={{ fontSize: 13, color: C.neutralDim, lineHeight: 1.5 }}>
                {card.description}
              </div>
            </button>
          );
        })}
      </div>
      {avisoTipo && (
        <div role="alert" style={{ fontSize: 13, color: C.neutralDim, marginTop: -20, marginBottom: 24 }}>
          {avisoTipo}
        </div>
      )}

      {/* Aviso Greenfield */}
      {localType === 'greenfield' && (
        <div
          role="note"
          style={{
            padding: '12px 16px',
            borderRadius: 8,
            backgroundColor: `${C.quantum}11`,
            border: `1px solid ${C.quantum}44`,
            marginBottom: 24,
            fontSize: 13,
            color: C.neutralDim,
          }}
        >
          <span style={{ color: C.quantum, fontWeight: 600 }}>Etapa de Ideação adicionada. </span>
          A etapa de Ideação (assistida por IA) foi incluída no fluxo desta jornada para
          capturar a visão de produto.
        </div>
      )}

      {/* Escopo de entrega (#6) — controla o que o pipeline gera */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.neutral, marginBottom: 4 }}>
          Escopo de entrega
        </div>
        <p style={{ fontSize: 12, color: C.neutralDim, marginBottom: 12 }}>
          Define o que o projeto entrega. Análise não gera código; Backend não gera telas.
        </p>
        <div
          role="radiogroup"
          aria-label="Escopo de entrega"
          className="grid gap-2"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))' }}
        >
          {[
            { id: 'fullstack', label: 'Fullstack', hint: 'Front + back' },
            { id: 'backend',   label: 'Backend',   hint: 'Só serviços/API' },
            { id: 'frontend',  label: 'Frontend',  hint: 'Só UI' },
            { id: 'analise',   label: 'Análise',   hint: 'Diagnóstico + diagramas' },
          ].map((s) => {
            const sel = deliveryScope === s.id;
            return (
              <button
                key={s.id}
                type="button"
                role="radio"
                aria-checked={sel}
                onClick={() => setDeliveryScope(s.id)}
                style={{
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: 8,
                  cursor: 'pointer',
                  backgroundColor: sel ? `${C.cyber}11` : C.blackElev,
                  border: `1px solid ${sel ? C.cyber : C.blackBorder}`,
                  transition: 'all 150ms',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: sel ? C.cyber : C.neutral }}>
                  {s.label}
                </div>
                <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 2 }}>{s.hint}</div>
              </button>
            );
          })}
        </div>
      </Card>

      {/* Modo do contrato (D1) — define se lacuna de especificação bloqueia ou só aponta */}
      <Card style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 14, fontWeight: 600, color: C.neutral, marginBottom: 4 }}>
          Modo do contrato
        </div>
        <p style={{ fontSize: 12, color: C.neutralDim, marginBottom: 12 }}>
          Como a fábrica reage quando o que foi pedido e o que foi entregue não fecham.
          Pode ser trocado depois.
        </p>
        <div
          role="radiogroup"
          aria-label="Modo do contrato"
          className="grid gap-2"
          style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
        >
          {CONTRACT_MODES.map((m) => {
            const sel = contractMode === m.id;
            const travado = m.id === 'strict' && deliveryMode === 'client';
            return (
              <button
                key={m.id}
                type="button"
                role="radio"
                aria-checked={sel}
                aria-label={m.label}
                disabled={travado}
                onClick={() => setContractMode(m.id)}
                style={{
                  textAlign: 'left',
                  padding: '10px 12px',
                  borderRadius: 8,
                  cursor: travado ? 'not-allowed' : 'pointer',
                  opacity: travado ? 0.6 : 1,
                  backgroundColor: sel ? `${C.cyber}11` : C.blackElev,
                  border: `1px solid ${sel ? C.cyber : C.blackBorder}`,
                  transition: 'all 150ms',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 600, color: sel ? C.cyber : C.neutral }}>
                  {m.label}
                </div>
                <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 2, lineHeight: 1.4 }}>
                  {m.hint}
                </div>
                {travado && (
                  <div style={{ fontSize: 11, color: C.warn, marginTop: 4, lineHeight: 1.4 }}>
                    Indisponível com a entrega finalizada no cliente: o contrato estrito exige a validação na plataforma.
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </Card>

      {/* Modo de entrega — só greenfield */}
      {localType === 'greenfield' && (
        <Card style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 14, fontWeight: 600, color: C.neutral, marginBottom: 4 }}>
            Modo de entrega
          </div>
          <p style={{ fontSize: 12, color: C.neutralDim, marginBottom: 12 }}>
            Onde o projeto gerado é validado antes de ficar pronto. Pode ser trocado depois.
          </p>
          <div
            role="radiogroup"
            aria-label="Modo de entrega"
            className="grid gap-2"
            style={{ gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}
          >
            {DELIVERY_MODES.map((m) => {
              const sel = deliveryMode === m.id;
              const travado = m.id === 'client' && contractMode === 'strict';
              return (
                <button
                  key={m.id}
                  type="button"
                  role="radio"
                  aria-checked={sel}
                  aria-label={m.label}
                  disabled={travado}
                  onClick={() => setDeliveryMode(m.id)}
                  style={{
                    textAlign: 'left',
                    padding: '10px 12px',
                    borderRadius: 8,
                    cursor: travado ? 'not-allowed' : 'pointer',
                    opacity: travado ? 0.6 : 1,
                    backgroundColor: sel ? `${C.cyber}11` : C.blackElev,
                    border: `1px solid ${sel ? C.cyber : C.blackBorder}`,
                    transition: 'all 150ms',
                  }}
                >
                  <div style={{ fontSize: 13, fontWeight: 600, color: sel ? C.cyber : C.neutral }}>
                    {m.label}
                  </div>
                  <div style={{ fontSize: 11, color: C.neutralMuted, marginTop: 2, lineHeight: 1.4 }}>
                    {m.hint}
                  </div>
                  {travado && (
                    <div style={{ fontSize: 11, color: C.warn, marginTop: 4, lineHeight: 1.4 }}>
                      Indisponível com o contrato estrito, que exige a validação na plataforma.
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {/* Campos de nome e descrição */}
      <Card style={{ marginBottom: 28 }}>
        <div style={{ marginBottom: 20 }}>
          <label
            htmlFor="project-name"
            style={{ display: 'block', fontSize: 13, fontWeight: 500, color: C.neutralDim, marginBottom: 8 }}
          >
            Nome do projeto <span style={{ color: C.hot }}>*</span>
          </label>
          <input
            id="project-name"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex.: Cadastro de Clientes"
            required
            aria-required="true"
            style={{
              width: '100%',
              padding: '10px 14px',
              backgroundColor: C.black,
              border: `1px solid ${C.blackBorder}`,
              borderRadius: 6,
              color: C.neutral,
              fontSize: 14,
            }}
          />
        </div>

        <div>
          <label
            htmlFor="project-description"
            style={{ display: 'block', fontSize: 13, fontWeight: 500, color: C.neutralDim, marginBottom: 8 }}
          >
            {isAnalise ? (
              <>Escopo da Análise <span style={{ color: C.hot }}>*</span></>
            ) : 'Descrição curta'}
          </label>
          {isAnalise && (
            <p style={{ fontSize: 12, color: C.neutralMuted, margin: '0 0 8px', lineHeight: 1.5 }}>
              Descreva <strong style={{ color: C.neutralDim }}>o que você precisa descobrir</strong>:
              perguntas a responder, hipóteses, recorte (o que entra e o que fica de fora) e o
              resultado esperado. É este texto que a análise vai endereçar.
            </p>
          )}
          <textarea
            id="project-description"
            value={description}
            placeholder={isAnalise
              ? 'Ex.: Avaliar a arquitetura do serviço de pagamentos — mapear dependências e dívida técnica, identificar riscos de escalabilidade e propor recomendações priorizadas...'
              : 'Contexto e objetivo principal do projeto'}
            rows={isAnalise ? 10 : 3}
            required={isAnalise}
            aria-required={isAnalise ? 'true' : undefined}
            aria-invalid={briefError ? 'true' : undefined}
            onChange={(e) => { setDescription(e.target.value); if (briefError) setBriefError(''); }}
            style={{
              width: '100%',
              padding: '10px 14px',
              backgroundColor: C.black,
              border: `1px solid ${briefError ? C.hot : C.blackBorder}`,
              borderRadius: 6,
              color: C.neutral,
              fontSize: 14,
              resize: 'vertical',
              lineHeight: 1.5,
            }}
          />
          {briefError && (
            <div role="alert" style={{ fontSize: 12, color: C.hot, marginTop: 6 }}>
              {briefError}
            </div>
          )}
        </div>
      </Card>

      {/* Ação */}
      <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Btn
          type="submit"
          variant="primary"
          size="lg"
          disabled={isLoading}
          aria-busy={isLoading}
        >
          {isLoading ? 'Salvando...' : 'Continuar — Repositório Git'}
        </Btn>
      </div>
    </form>
  );
}
